import { useState } from "react";
import { motion } from "framer-motion";
import { Crosshair, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import SmartUploadMenu from "@/components/SmartUploadMenu";
import { IntakeModal } from "@/components/intake/IntakePanel";
import OutreachModal from "@/components/OutreachModal";
import { PendingLeadsBanner } from "./agent-actions/PendingLeadsBanner";
import { HuntFarmControls } from "./agent-actions/HuntFarmControls";
import { ResultsSummaryPanel } from "./agent-actions/ResultsSummaryPanel";
import { AdvisorModal } from "./agent-actions/AdvisorModal";
import { AdvisorWelcomeModal } from "./agent-actions/AdvisorWelcomeModal";
import { HuntResultsModal } from "./agent-actions/HuntResultsModal";
import { useAdvisor } from "./agent-actions/useAdvisor";
import { useProspectingSession } from "./agent-actions/useProspectingSession";

const AgentActionsCenter = ({ userId, onRefresh }) => {
  const session = useProspectingSession(userId, onRefresh);
  const advisor = useAdvisor(userId, onRefresh);
  const [expandedLeadIndex, setExpandedLeadIndex] = useState(null);
  const [outreachLead, setOutreachLead] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);

  const { results } = session;
  const resultsMode = results?.mode?.toLowerCase();
  const needsDisposition = (resultsMode === "hunt" && !session.canRunNewHunt) || (resultsMode === "farm" && !session.canRunNewFarm);

  const openAdvisor = () => {
    session.setShowResults(false);
    advisor.toggle();
  };

  return (
    <>
      <motion.div
        className="glass-card p-6 min-h-[500px] max-h-[80vh] flex flex-col relative overflow-hidden"
        data-testid="agent-actions-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="absolute inset-0 pointer-events-none" />

        <div className="mb-6 relative z-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-ism-teal/20 to-ism-teal/10 border border-ism-teal/20">
                <Crosshair className="h-6 w-6 text-ism-teal" />
              </div>
              <div>
                <h2 className="text-xl tracking-tight"><span className="text-ism-teal">Agent Actions</span></h2>
                <p className="text-sm text-gray-500">Self-Service Sales Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setIntakeOpen(true)} className="border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10" data-testid="agent-actions-intake-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Customers &amp; prospects
              </Button>
              <SmartUploadMenu userId={userId} onUploadComplete={onRefresh} />
            </div>
          </div>
        </div>

        {!advisor.open && !session.showResults && (
          <>
            {(session.huntPending > 0 || session.farmPending > 0) && (
              <PendingLeadsBanner huntPending={session.huntPending} farmPending={session.farmPending} onView={session.viewPending} onDismissAll={session.dismissAllPending} />
            )}
            <HuntFarmControls hunt={session.hunt} farm={session.farm} advisor={{ onToggle: openAdvisor, processing: advisor.processing }} />
            <motion.div className="glass-card-light rounded-xl p-4 mt-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <p className="text-sm text-gray-400 leading-relaxed">
                Click any action to activate <span className="text-ism-teal">AI-powered</span> sales intelligence. Results will appear here with real-time web data.
              </p>
            </motion.div>
          </>
        )}

        {session.showResults && !advisor.open && results && (
          <ResultsSummaryPanel results={results} savedCount={session.savedLeads.size} dismissedCount={session.dismissedLeads.size}
            onViewResults={() => session.setShowResultsModal(true)} onBack={() => session.setShowResults(false)} />
        )}
      </motion.div>

      <AdvisorModal
        open={advisor.open}
        userId={userId}
        messages={advisor.messages}
        processing={advisor.processing}
        question={advisor.question}
        setQuestion={advisor.setQuestion}
        onSend={advisor.ask}
        onClose={advisor.toggle}
      />

      <HuntResultsModal
        open={session.showResultsModal}
        userId={userId}
        results={results}
        savedLeads={session.savedLeads}
        dismissedLeads={session.dismissedLeads}
        dispositionOrder={session.dispositionOrder}
        savingLead={session.savingLead}
        expandedLeadIndex={expandedLeadIndex}
        needsDisposition={needsDisposition}
        onClose={() => session.setShowResultsModal(false)}
        onSave={session.saveLead}
        onDismiss={session.dismissLead}
        onOutreach={(lead) => setOutreachLead(lead)}
        onToggleExpand={(idx) => setExpandedLeadIndex(expandedLeadIndex === idx ? null : idx)}
        onRerun={session.rerunHunt}
      />

      <AdvisorWelcomeModal open={advisor.showWelcome} onClose={advisor.closeWelcome} />
      <IntakeModal open={intakeOpen} onClose={() => setIntakeOpen(false)} userId={userId} onCommitted={onRefresh} />

      <OutreachModal isOpen={!!outreachLead} onClose={() => setOutreachLead(null)} lead={outreachLead} userId={userId} />
    </>
  );
};

export default AgentActionsCenter;
